import { FragmentFormActions } from '@app/modules/fragments/screens/add/FragmentFormActions';
import { getFragmentValidation } from '@app/modules/fragments/screens/add/validation';
import { SectionHeader } from '@app/modules/social-export/screens/manage/platforms/FormSection';
import { zodResolver } from '@hookform/resolvers/zod';
import { Station } from '@mi-front/core';
import { Box, Grid, Stack } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { FormContainer, TextFieldElement, useForm, useWatch } from 'react-hook-form-mui';
import { useIntl } from 'react-intl';
import { DescriptionFooterSelector } from './DescriptionFooterSelector';
import { ExportControl } from './export/ExportControl';
import { FragmentSocialExportFields } from './social-export/FragmentSocialExportFields';
import { FragmentsTagInput } from './tags/FragmentsTagInput';
import { CreateFragmentFormValues } from './useFragmentForm';

interface Props {
  station: Station;
  allowExternalExport: boolean;
  initialValues?: Partial<CreateFragmentFormValues>;
  onCreate: (data: CreateFragmentFormValues) => Promise<unknown>;
  /** Single cancel button. Use this OR both onDiscard and onDraft. */
  onCancel?: () => void;
  /** Discard and Draft buttons. When both provided, replace the single Cancel. */
  onDiscard?: () => void;
  onDraft?: () => void;
  enableCreate?: boolean;
  onChangedValues?: (values: CreateFragmentFormValues) => void;
}

export function CreateFragmentForm({
  station,
  initialValues,
  onCreate,
  onCancel,
  onDiscard,
  onDraft,
  allowExternalExport,
  enableCreate = true,
  onChangedValues,
}: Props) {
  const { $t } = useIntl();
  const schema = useMemo(() => getFragmentValidation((msg) => $t(msg)), [$t]);

  const formContext = useForm<CreateFragmentFormValues>({
    defaultValues: {
      stationId: station.id,
      title: '',
      description: '',
      tags: '',
      doExternalExport: allowExternalExport,
      platformUpload: {},
      ...initialValues,
    },
  });

  // Watch form changes and notify parent (only when user has changed something, so we don't repopulate the store on mount after a reset)
  const watchedValues = useWatch({ control: formContext.control });
  const isDirty = formContext.formState.isDirty;

  useEffect(() => {
    if (isDirty && watchedValues && onChangedValues) {
      onChangedValues(watchedValues as CreateFragmentFormValues);
    }
  }, [isDirty, watchedValues, onChangedValues]);

  useEffect(() => {
    formContext.setFocus('title');
  }, []);

  return (
    <FormContainer
      formContext={formContext}
      resolver={zodResolver(schema)}
      onSuccess={async (data) => {
        await onCreate({
          stationId: data.stationId,
          title: data.title,
          description: data.description,
          tags: data.tags,
          doExternalExport: data.doExternalExport,
          platformUpload: data.platformUpload,
        });

        formContext.reset();
      }}
      FormProps={{
        style: {
          flexGrow: 1,
          height: '100%',
        },
      }}
    >
      <Box
        sx={{
          p: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <SectionHeader
          title={$t({
            id: 'fragments.form.header',
            defaultMessage: 'Crear fragmento',
          })}
        />
        <Box sx={{ flexGrow: 1 }}>
          <Stack spacing={3}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextFieldElement
                  name="title"
                  label={$t({
                    id: 'fragments.props.title',
                    defaultMessage: 'Título',
                  })}
                  placeholder={$t({
                    id: 'fragments.form.title.placeholder',
                    defaultMessage: 'Entrevista con Messi',
                  })}
                  fullWidth
                  required
                  multiline
                  minRows={2}
                  maxRows={3}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextFieldElement
                  name="description"
                  label={$t({
                    id: 'fragments.props.description',
                    defaultMessage: 'Descripción',
                  })}
                  placeholder={$t({
                    id: 'fragments.form.description.placeholder',
                    defaultMessage: 'Grabada en el estadio durante el Mundial',
                  })}
                  multiline
                  minRows={3}
                  maxRows={8}
                  fullWidth
                  helperText={<DescriptionFooterSelector />}
                />
              </Grid>
            </Grid>
            <FragmentsTagInput />
            <Box>
              {allowExternalExport && <ExportControl />}
              <FragmentSocialExportFields station={station} />
            </Box>
          </Stack>
        </Box>
        <Box mt="auto">
          <FragmentFormActions
            saveLabel={$t({
              id: 'common.actions.createEntity',
              defaultMessage: 'Crear',
            })}
            onCancel={onCancel}
            onDiscard={onDiscard}
            onDraft={onDraft}
            disableSave={!enableCreate}
          />
        </Box>
      </Box>
    </FormContainer>
  );
}
